import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X, Clock } from "lucide-react";
import { TimeSlot } from "@/types/calendarManagementTypes";

interface TimeSlotSelectorProps {
  timeSlots: TimeSlot[];
  onChange: (slots: TimeSlot[]) => void;
  defaultTimeSlot?: TimeSlot;
}


export function TimeSlotSelector({ 
  timeSlots, 
  onChange,
  defaultTimeSlot = { startTime: '', endTime: '' }
}: TimeSlotSelectorProps) {
  const [startTime, setStartTime] = useState(defaultTimeSlot.startTime);
  const [endTime, setEndTime] = useState(defaultTimeSlot.endTime);

  const addTimeSlot = () => {
    if (startTime && endTime) {
      // Validate time range
      if (startTime >= endTime) {
        // You might want to show an error message here
        return;
      }
      
      const newSlots = [...timeSlots, { startTime, endTime }];
      onChange(newSlots);
      // Reset input fields after adding
      setStartTime('');
      setEndTime('');
    }
  };

  const removeTimeSlot = (index: number) => {
    // Don't allow removing the last slot if it's the only one
    if (timeSlots.length === 1) {
      onChange([defaultTimeSlot]); // Reset to default empty slot
      return;
    }
    
    const newSlots = timeSlots.filter((_, i) => i !== index);
    onChange(newSlots);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-4">
        <div className="flex-1 space-y-2">
          <Input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="h-11 transition-all border-2 hover:border-primary/50 focus:border-primary rounded-lg"
          />
        </div>
        <div className="flex-1 space-y-2">
          <Input
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="h-11 transition-all border-2 hover:border-primary/50 focus:border-primary rounded-lg"
          />
        </div>
        <Button 
          type="button"
          onClick={addTimeSlot}
          disabled={!startTime || !endTime}
          className="h-11 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 bg-gradient-to-r from-action-500 to-action-600 text-primary-foreground hover:from-action-600 hover:to-action-700 rounded-lg"
        >
          <Plus className="mr-2 h-5 w-5" />
          Add Slot
        </Button>
      </div>

      <div className="space-y-2">
        {timeSlots.map((slot, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border-2 border-secondary/30 hover:border-primary/50 transition-all group"
          >
            <span className="flex items-center gap-2 font-medium text-foreground">
              <Clock className="h-4 w-4 text-primary" />
              {slot.startTime} - {slot.endTime}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => removeTimeSlot(index)}
              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/20 hover:text-destructive rounded-lg"
              disabled={timeSlots.length === 1 && !slot.startTime && !slot.endTime} // Disable delete for empty default slot
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}